import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  Tenant,
  TenantCreateFormData,
  TenantUpdateFormData,
} from "../../schemas/tenantSchema";

export interface TenantsResponse {
  tenants: Tenant[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface TenantFilters {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface UpdateTenantPayload {
  id: string;
  tenant: TenantUpdateFormData;
}

const GET_TENANTS = gql`
  query GetTenants($page: Int, $pageSize: Int, $search: String) {
    tenants(page: $page, pageSize: $pageSize, search: $search) {
      tenants {
        id
        companyName
        name
        username
        email
        phoneNumber
        address
        isPremium
        createdAt
        updatedAt
        createdBy
      }
      page
      pageSize
      total
      totalPages
      hasNextPage
      hasPrevPage
    }
  }
`;

const GET_TENANT_BY_ID = gql`
  query GetTenantById($id: ID!) {
    tenant(id: $id) {
      id
      companyName
      name
      username
      email
      phoneNumber
      address
      isPremium
      createdAt
      updatedAt
      createdBy
    }
  }
`;

const CREATE_TENANT = gql`
  mutation CreateTenant($input: CreateTenantInput!) {
    createTenant(input: $input)
  }
`;

const UPDATE_TENANT = gql`
  mutation UpdateTenant($id: ID!, $input: UpdateTenantInput!) {
    updateTenant(id: $id, input: $input)
  }
`;

const DELETE_TENANTS = gql`
  mutation DeleteTenants($ids: [ID!]!) {
    deleteTenants(ids: $ids)
  }
`;

export const tenantService = {
  getAll: async (filters: TenantFilters = {}): Promise<TenantsResponse> => {
    const { data } = await client.query({
      query: GET_TENANTS,
      variables: filters,
      fetchPolicy: "network-only",
    });
    return data.tenants;
  },

  getById: async (id: string): Promise<Tenant> => {
    const { data } = await client.query({
      query: GET_TENANT_BY_ID,
      variables: { id },
      fetchPolicy: "network-only",
    });
    return data.tenant;
  },

  create: async (input: TenantCreateFormData): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: CREATE_TENANT,
      variables: { input },
    });
    return data.createTenant;
  },

  update: async (payload: UpdateTenantPayload): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: UPDATE_TENANT,
      variables: {
        id: payload.id,
        input: payload.tenant,
      },
    });
    return data.updateTenant;
  },

  remove: async (ids: string[]): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_TENANTS,
      variables: { ids },
    });
    return data.deleteTenants;
  },
};
